import { CreqAuditCandidateStatus, HubFeature } from '@ats/api/cm';
import { LocDef, LocDefs, LocMap } from '../../models';
import { SYSTEM_ENUM_ROOT } from '../shared-data';

const dpRoot = `${SYSTEM_ENUM_ROOT}|Candidate Audit History Status|`;

type SharedKeys = Pick<
	LocDef,
	'type' | 'displayType' | 'htmlAllowed' | 'portal' | 'shared' | 'feature'
>;

const sharedLocData: Pick<LocDef, 'displayType' | 'htmlAllowed' | 'shared' | 'feature'> = {
	displayType: 'System',
	htmlAllowed: false,
	shared: true,
	feature: HubFeature.OtherRequirementUploadCandidateAccessAudits,
};

const sharedSpLocData: SharedKeys = {
	...sharedLocData,
	portal: 'SP',
};

const sharedCpLocData: SharedKeys = {
	...sharedLocData,
	portal: 'CP',
};

export type SPCreqAuditCandidateStatusLocs =
	| `SPCreqAuditCandidateStatusCompleted`
	| `SPCreqAuditCandidateStatusInProgress`
	| `SPCreqAuditCandidateStatusNew`
	| `SPCreqAuditCandidateStatusOnHold`
	| `SPCreqAuditCandidateStatusPastDue`
	| `SPCreqAuditCandidateStatusNone`
	| `SPCreqAuditCandidateStatusUnassigned`;

export const spCreqAuditCandidateStatusEnumLocs: LocDefs<SPCreqAuditCandidateStatusLocs> = {
	SPCreqAuditCandidateStatusCompleted: {
		defaultValue: 'Completed',
		displayPath: `${dpRoot}${CreqAuditCandidateStatus.Completed}`,
		description: `Candidate audit history status: Completed`,
		...sharedSpLocData,
	},
	SPCreqAuditCandidateStatusInProgress: {
		defaultValue: 'In Progress',
		displayPath: `${dpRoot}${CreqAuditCandidateStatus.InProgress}`,
		description: `Candidate audit history status: InProgress`,
		...sharedSpLocData,
	},
	SPCreqAuditCandidateStatusNew: {
		defaultValue: 'New Audits',
		displayPath: `${dpRoot}${CreqAuditCandidateStatus.New}`,
		description: `Candidate audit history status: New`,
		...sharedSpLocData,
	},
	SPCreqAuditCandidateStatusNone: {
		defaultValue: 'None',
		displayPath: `${dpRoot}${CreqAuditCandidateStatus.None}`,
		description: `Candidate audit history status: None`,
		...sharedSpLocData,
	},
	SPCreqAuditCandidateStatusUnassigned: {
		defaultValue: 'Unassigned',
		displayPath: `${dpRoot}${CreqAuditCandidateStatus.Unassigned}`,
		description: `Candidate audit history status: Unassigned`,
		...sharedSpLocData,
	},
	SPCreqAuditCandidateStatusOnHold: {
		defaultValue: 'On Hold',
		displayPath: `${dpRoot}${CreqAuditCandidateStatus.OnHold}`,
		description: `Candidate audit history status: OnHold`,
		...sharedSpLocData,
	},
	SPCreqAuditCandidateStatusPastDue: {
		defaultValue: 'Past Due',
		displayPath: `${dpRoot}${CreqAuditCandidateStatus.PastDue}`,
		description: `Candidate audit history status: PastDue`,
		...sharedSpLocData,
	},
};

export type CPCreqAuditCandidateStatusLocs =
	| `CPCreqAuditCandidateStatusCompleted`
	| `CPCreqAuditCandidateStatusInProgress`
	| `CPCreqAuditCandidateStatusNew`
	| `CPCreqAuditCandidateStatusNone`
	| `CPCreqAuditCandidateStatusUnassigned`
	| `CPCreqAuditCandidateStatusOnHold`
	| `CPCreqAuditCandidateStatusPastDue`;

export const cpCreqAuditCandidateStatusEnumLocs: LocDefs<CPCreqAuditCandidateStatusLocs> = {
	CPCreqAuditCandidateStatusCompleted: {
		defaultValue: 'Completed',
		displayPath: `${dpRoot}${CreqAuditCandidateStatus.Completed}`,
		description: `Candidate audit history status: Completed`,
		...sharedCpLocData,
	},
	CPCreqAuditCandidateStatusInProgress: {
		defaultValue: 'In Progress',
		displayPath: `${dpRoot}${CreqAuditCandidateStatus.InProgress}`,
		description: `Candidate audit history status: InProgress`,
		...sharedCpLocData,
	},
	CPCreqAuditCandidateStatusNew: {
		defaultValue: 'New Audits',
		displayPath: `${dpRoot}${CreqAuditCandidateStatus.New}`,
		description: `Candidate audit history status: New`,
		...sharedCpLocData,
	},
	CPCreqAuditCandidateStatusNone: {
		defaultValue: 'None',
		displayPath: `${dpRoot}${CreqAuditCandidateStatus.None}`,
		description: `Candidate audit history status: None`,
		...sharedCpLocData,
	},
	CPCreqAuditCandidateStatusUnassigned: {
		defaultValue: 'Unassigned',
		displayPath: `${dpRoot}${CreqAuditCandidateStatus.Unassigned}`,
		description: `Candidate audit history status: Unassigned`,
		...sharedCpLocData,
	},
	CPCreqAuditCandidateStatusOnHold: {
		defaultValue: 'On Hold',
		displayPath: `${dpRoot}${CreqAuditCandidateStatus.OnHold}`,
		description: `Candidate audit history status: OnHold`,
		...sharedCpLocData,
	},
	CPCreqAuditCandidateStatusPastDue: {
		defaultValue: 'Past Due',
		displayPath: `${dpRoot}${CreqAuditCandidateStatus.PastDue}`,
		description: `Candidate audit history status: PastDue`,
		...sharedCpLocData,
	},
};

export type SPCreqAuditCandidateStatusLocType = LocMap<SPCreqAuditCandidateStatusLocs>;
export type CPCreqAuditCandidateStatusLocType = LocMap<CPCreqAuditCandidateStatusLocs>;

export const spCreqAuditCandidateStatusKeys: {
	[key in CreqAuditCandidateStatus]: keyof SPCreqAuditCandidateStatusLocType;
} = {
	[CreqAuditCandidateStatus.Completed]: 'SPCreqAuditCandidateStatusCompleted',
	[CreqAuditCandidateStatus.InProgress]: 'SPCreqAuditCandidateStatusInProgress',
	[CreqAuditCandidateStatus.New]: 'SPCreqAuditCandidateStatusNew',
	[CreqAuditCandidateStatus.None]: 'SPCreqAuditCandidateStatusNone',
	[CreqAuditCandidateStatus.Unassigned]: 'SPCreqAuditCandidateStatusUnassigned',
	[CreqAuditCandidateStatus.OnHold]: 'SPCreqAuditCandidateStatusOnHold',
	[CreqAuditCandidateStatus.PastDue]: 'SPCreqAuditCandidateStatusPastDue',
};

export const cpCreqAuditCandidateStatusKeys: {
	[key in CreqAuditCandidateStatus]: keyof CPCreqAuditCandidateStatusLocType;
} = {
	[CreqAuditCandidateStatus.Completed]: 'CPCreqAuditCandidateStatusCompleted',
	[CreqAuditCandidateStatus.InProgress]: 'CPCreqAuditCandidateStatusInProgress',
	[CreqAuditCandidateStatus.New]: 'CPCreqAuditCandidateStatusNew',
	[CreqAuditCandidateStatus.None]: 'CPCreqAuditCandidateStatusNone',
	[CreqAuditCandidateStatus.Unassigned]: 'CPCreqAuditCandidateStatusUnassigned',
	[CreqAuditCandidateStatus.OnHold]: 'CPCreqAuditCandidateStatusOnHold',
	[CreqAuditCandidateStatus.PastDue]: 'CPCreqAuditCandidateStatusPastDue',
};
